// SAMURAI — films, releases, viral & social entries
// Keep every piece of client-supplied metadata here (director, agency, YouTube ID, thumb).

// YouTube thumbnail helpers.
// `hqdefault.jpg` is guaranteed to exist for every video (480x360, 4:3 with tiny letterbox).
// Rendered inside a 16:9 container with object-cover, the letterbox crops away cleanly.
// `sddefault`/`mqdefault` are used as fallbacks when available.
export const yt = (id) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
export const ytFallback = (id) => `https://i.ytimg.com/vi/${id}/mqdefault.jpg`;

// Featured releases (Home page carousel) — Love Beauty & Planet first as latest
export const LATEST_RELEASES = [
  {
    id: "love_beauty_planet",
    title: "Love Beauty & Planet",
    type: "Hybrid",
    director: "Sunetro Lahiri",
    agency: "Ogilvy",
    youtubeId: "eYpTC6UhwOo",
    image: "/thumbs/love-beauty-planet.jpg",
    imageFallback: ytFallback("eYpTC6UhwOo"),
  },
  {
    id: "horlicks",
    title: "Horlicks",
    type: "AI-led",
    director: "Jessica Sadana",
    agency: "Toaster",
    youtubeId: "0I0QhAY3WVQ",
    image: "/thumbs/horlicks.jpg",
    imageFallback: ytFallback("0I0QhAY3WVQ"),
  },
  {
    id: "godrej",
    title: "Godrej Properties",
    type: "AI-led",
    agency: "AA",
    youtubeId: "n37kFUaiTwU",
    image: "/thumbs/godrej.webp",
    imageFallback: ytFallback("n37kFUaiTwU"),
  },
];

// Featured release (Home page — kept for legacy imports)
export const FEATURED = LATEST_RELEASES[0];

// Work grid — new ordering: LBP → Horlicks → Close Up → Comfort → Bru → Godrej → Horlicks Milkshake → Visit Health → Jawa
export const WORKS = [
  {
    id: "love_beauty_planet",
    title: "LBP Cuticles",
    type: "Hybrid",
    year: 2026,
    director: "Sunetro Lahiri",
    agency: "Ogilvy",
    youtubeId: "eYpTC6UhwOo",
    image: "/thumbs/love-beauty-planet.jpg",
    imageFallback: ytFallback("eYpTC6UhwOo"),
    description:
      "Beauty that loves the planet back. A burst of colour, community and everyday joy. Set in a sun-lit village fair, the film wraps the brand's feel-good promise in warmth, movement and playful energy, where looking good and caring for the planet live happily side by side.",
  },
  {
    id: "horlicks",
    title: "Horlicks",
    type: "AI-led",
    year: 2026,
    director: "Jessica Sadana",
    agency: "Toaster",
    youtubeId: "0I0QhAY3WVQ",
    image: "/thumbs/horlicks.jpg",
    imageFallback: ytFallback("0I0QhAY3WVQ"),
    description:
      "Where friendships find their favourite corner. School memories are rarely about classrooms. They are built between conversations, laughter, and the spaces where friendships quietly grow. Set within that familiar world, the film captures youthful energy with warmth, spontaneity, and a sense of nostalgia that feels instantly recognisable.",
  },
  {
    id: "closeup",
    title: "Close Up",
    type: "AI-led",
    year: 2025,
    director: "Jessica Sadana",
    agency: "TBWA",
    youtubeId: "BjadOnk3iQY",
    image: "/thumbs/closeup.jpeg",
    imageFallback: ytFallback("BjadOnk3iQY"),
    description:
      "Every connection begins with confidence. Confidence often lives in the smallest moments. A smile exchanged across a room. A conversation that lingers a little longer. This film embraces those quiet sparks through expressive and dynamic visuals, reflecting the youthful energy that has always defined the brand.",
  },
  {
    id: "comfort",
    title: "Comfort",
    type: "AI-led",
    year: 2025,
    director: "Jessica Sadana",
    agency: "Ogilvy",
    youtubeId: "ZTr8Pe4V57M",
    image: "/thumbs/comfort.jpeg",
    imageFallback: ytFallback("ZTr8Pe4V57M"),
    description:
      "Freshness finds a way. The monsoon has a rhythm of its own. Damp mornings, unexpected showers, and clothes that carry the scent of rain. This campaign finds beauty within those familiar moments, transforming an everyday challenge into a story filled with freshness, optimism, and effortless charm.",
  },
  {
    id: "bru",
    title: "Bru",
    type: "AI-led",
    year: 2025,
    director: "Trupti Shigwan",
    agency: "Ogilvy",
    youtubeId: "jQxK_F8iCxA",
    image: yt("jQxK_F8iCxA"),
    imageFallback: ytFallback("jQxK_F8iCxA"),
    description:
      "Every cup carries a journey. Coffee is as much about where it comes from as the pause it creates. Inspired by the landscapes, people, and traditions behind every cup, the film unfolds as a quiet journey through craft, culture, and everyday ritual.",
  },
  {
    id: "godrej",
    title: "Godrej Properties",
    type: "AI-led",
    year: 2025,
    agency: "AA",
    youtubeId: "n37kFUaiTwU",
    image: "/thumbs/godrej.webp",
    imageFallback: ytFallback("n37kFUaiTwU"),
    description:
      "Innovation, made human. Innovation becomes meaningful when it feels effortless. Created to communicate a complex offering with clarity, the film balances information with elegant visual storytelling, making technology feel accessible, refined, and deeply human.",
  },
  {
    id: "horlicks_milkshake",
    title: "Horlicks Milkshake",
    type: "AI-led",
    year: 2026,
    director: "Jessica Sadana",
    agency: "Toaster",
    youtubeId: "nNKoAvYqjQ4",
    image: "/thumbs/horlicks-milkshake.webp",
    imageFallback: ytFallback("nNKoAvYqjQ4"),
    description:
      "Some rituals never change. A comforting ritual, reimagined for a new generation. Created for Horlicks Milkshake, the film captures familiar moments through warmth, movement, and a visual language that feels playful without losing its sense of comfort. A celebration of everyday refreshment told with quiet simplicity.",
  },
  {
    id: "visit_health",
    title: "Visit Health",
    type: "AI-led",
    year: 2025,
    director: "Priyanku S",
    directorLabel: "Gen AI Dir",
    agency: "Punt Partners",
    youtubeId: "nupCs-Hm_5A",
    image: "/thumbs/visit-health.webp",
    imageFallback: ytFallback("nupCs-Hm_5A"),
    description:
      "Care begins long before treatment. Wellbeing is shaped by the decisions we make every day. Through an optimistic and human-centred narrative, the film explores how accessible healthcare can become part of everyday life, encouraging healthier choices with empathy and simplicity.",
  },
  {
    id: "jawa",
    title: "Jawa",
    type: "AI-led",
    year: 2026,
    director: "Priyanku Sarmah",
    directorLabel: "Gen AI Dir",
    agency: "",
    youtubeId: "XdIV7XxMmMU",
    image: yt("XdIV7XxMmMU"),
    imageFallback: ytFallback("XdIV7XxMmMU"),
    description:
      "Freedom has always sounded like this. The road has always promised more than a destination. It offers freedom, discovery, and stories waiting to unfold beyond the horizon. Through cinematic landscapes and purposeful movement, the film celebrates the enduring spirit that has made Jawa an icon across generations.",
  },
];

// Viral & Social films
export const VIRAL_SOCIAL = [
  {
    id: "bw",
    title: "Jaguar",
    type: "AI-led",
    year: 2026,
    director: "Priyanku S",
    directorLabel: "Gen AI Dir",
    video: "/viral/bw.mp4",
    youtubeId: "IXK6Jkp2_wE",
  },
  {
    id: "monsoon",
    title: "Mumbai Monsoon Madness",
    type: "AI-led",
    year: 2026,
    director: "Priyanku S",
    directorLabel: "Gen AI Dir",
    video: "/viral/monsoon.mp4",
    youtubeId: "er11YuZwrho",
  },
  {
    id: "ai-film",
    title: "AI Can Make the Film But Not the Story",
    type: "AI-led",
    year: 2026,
    director: "Priyanku S",
    directorLabel: "Gen AI Dir",
    video: "/viral/ai-film.mp4",
    youtubeId: "AyT3QoIoD0E",
  },
];
